import { timestampDate, type Timestamp } from '@bufbuild/protobuf/wkt'

import { LeadReferralStatusEnum } from '@/gen/cortado/v1/referrals_pb'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'

/** Codes are 6 characters shown as ABC-DEF; typing ignores case, spaces and dashes. */
export function normalizeReferralCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function isCompleteReferralCode(raw: string): boolean {
  return normalizeReferralCode(raw).length === 6
}

/**
 * Mirrors the backend's Send checks: an archived, booked or won lead stays
 * put, and a lead another business accepted can't go out again.
 */
export function canReferLead(inquiry: { archivedAt?: unknown; bookingId?: string; stage: InquiryStageEnum }): boolean {
  return (
    !inquiry.archivedAt &&
    !inquiry.bookingId &&
    inquiry.stage !== InquiryStageEnum.INQUIRY_STAGE_ACCEPTED &&
    inquiry.stage !== InquiryStageEnum.INQUIRY_STAGE_REFERRED
  )
}

export const REFERRAL_STATUS_LABELS: Record<LeadReferralStatusEnum, string> = {
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_UNSPECIFIED]: 'Unknown',
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_PENDING]: 'Pending',
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_ACCEPTED]: 'Accepted',
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_REJECTED]: 'Rejected',
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_WITHDRAWN]: 'Withdrawn',
  [LeadReferralStatusEnum.LEAD_REFERRAL_STATUS_EXPIRED]: 'Expired',
}

/** "2 days left", "5 hours left", "40 minutes left", or "Expired". */
export function referralTimeLeft(expiresAt: Timestamp | undefined, now: Date = new Date()): string {
  if (!expiresAt) return ''
  const ms = timestampDate(expiresAt).getTime() - now.getTime()
  if (ms <= 0) return 'Expired'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${Math.max(minutes, 1)} minute${minutes === 1 ? '' : 's'} left`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} left`
  const days = Math.floor(hours / 24)
  return `${days} days left`
}

/** The event date in the event's own timezone when it has one. */
export function formatReferralEventDate(date: Timestamp | undefined, timeZone?: string): string {
  if (!date) return 'No date yet'
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
  try {
    return timestampDate(date).toLocaleDateString(undefined, timeZone ? { ...options, timeZone } : options)
  } catch {
    return timestampDate(date).toLocaleDateString(undefined, options)
  }
}
